import { CreateLeaveRequestInput, LeaveRequest, LeaveStatus } from '../types/leave';
import { generateClient } from 'aws-amplify/api';
import { announceLeaveSubmitted } from './leaveNotifications';

const LEAVE_STORAGE_KEY = 'creyenteone:leave-requests:v1';
const DELETED_LEAVE_IDS_KEY = 'creyenteone:leave-deleted-ids:v1';
let client: any = null;

const getClient = () => {
  if (!client) client = generateClient();
  return client;
};

const getAuthMode = () => ({ authMode: 'userPool' as const });
const getData = (result: any) => ('data' in result ? result.data : null);

// decisionReason is *requested*, not just written: updateLeave has always sent it, but with
// it missing from this selection set nothing ever read it back. That is why the reason an
// employee gave for cancelling arrived at their manager as an em dash.
const leaveFields = `
  id employeeEmail employeeName department type startDate endDate reason status createdAt session
  decisionReason updatedBy updatedAt
`;

const listLeavesQuery = /* GraphQL */ `
  query ListLeaves($limit: Int, $nextToken: String) {
    listLeaves(limit: $limit, nextToken: $nextToken) {
      items { ${leaveFields} }
      nextToken
    }
  }
`;

const createLeaveMutation = /* GraphQL */ `
  mutation CreateLeave($input: CreateLeaveInput!) {
    createLeave(input: $input) { ${leaveFields} }
  }
`;

const updateLeaveMutation = /* GraphQL */ `
  mutation UpdateLeave($input: UpdateLeaveInput!) {
    updateLeave(input: $input) { ${leaveFields} }
  }
`;

// Takes `id`, not an input object — matching the schema. The previous shape
// (`deleteLeave(input: $input)`) referenced a DeleteLeaveInput that never existed, so every
// call failed validation and the deletion only ever happened in localStorage.
const deleteLeaveMutation = /* GraphQL */ `
  mutation DeleteLeave($id: ID!) {
    deleteLeave(id: $id) { id }
  }
`;

const readLeaves = (): LeaveRequest[] => {
  try {
    const raw = localStorage.getItem(LEAVE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeLeaves = (leaves: LeaveRequest[]) => {
  localStorage.setItem(LEAVE_STORAGE_KEY, JSON.stringify(leaves));
};

const getDeletedIds = (): Set<string> => {
  try {
    const raw = localStorage.getItem(DELETED_LEAVE_IDS_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const addDeletedId = (id: string) => {
  const ids = getDeletedIds();
  ids.add(id);
  localStorage.setItem(DELETED_LEAVE_IDS_KEY, JSON.stringify([...ids]));
};

export class LeaveService {
  static async listLeaves(): Promise<LeaveRequest[]> {
    const deletedIds = getDeletedIds();
    try {
      const result = await getClient().graphql({
        query: listLeavesQuery,
        variables: { limit: 500 },
        ...getAuthMode(),
      } as any);
      const items = (getData(result)?.listLeaves?.items || []).filter((l: LeaveRequest) => !deletedIds.has(l.id));
      return items.sort((a: LeaveRequest, b: LeaveRequest) => b.createdAt.localeCompare(a.createdAt));
    } catch (error) {
      console.warn('[Leave] listLeaves using local fallback:', error);
      return readLeaves().filter(l => !deletedIds.has(l.id)).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    }
  }

  /**
   * File a leave request.
   *
   * Announced to the employee, their manager and HR — but only when the SERVER accepted it. The
   * email used to be sent unconditionally at the end of this method, below the localStorage
   * fallback, so a failed mutation still told a manager to go and review a request that existed
   * in one browser tab and nowhere else. A leave nobody else can see is not a leave anybody can
   * approve.
   *
   * `managerName` is for the email only; the row has no such column, and the GraphQL input is
   * built field by field above precisely so a field the type does not declare cannot reach it.
   */
  static async createLeave(
    input: CreateLeaveRequestInput & { managerEmail?: string; managerName?: string }
  ): Promise<LeaveRequest> {
    let created: LeaveRequest | null = null;
    /* Distinct from `created` being set, which the local fallback also does. */
    let serverCreated = false;
    try {
      const result = await getClient().graphql({
        query: createLeaveMutation,
        variables: { input: { employeeEmail: input.employeeEmail, employeeName: input.employeeName, department: input.department, type: input.type, startDate: input.startDate, endDate: input.endDate, reason: input.reason, session: input.session || 'FULL' } },
        ...getAuthMode(),
      } as any);
      created = getData(result)?.createLeave;
      if (created) {
        serverCreated = true;
        window.dispatchEvent(new Event('leave-requests-updated'));
      }
    } catch (error) {
      console.warn('[Leave] createLeave using local fallback:', error);
    }

    if (!created) {
      const now = new Date().toISOString();
      created = {
        ...input,
        session: input.session || 'FULL',
        id: `leave-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        status: LeaveStatus.SUBMITTED,
        createdAt: now,
      };
      const next = [created, ...readLeaves()];
      writeLeaves(next);
      window.dispatchEvent(new Event('leave-requests-updated'));
    }

    /*
     * Announced only when the server took it.
     *
     * The local fallback row exists in one browser and is invisible to the manager it would be
     * telling to review it — so the honest thing is silence, and the applicant seeing their own
     * request on their own screen, until the write really lands.
     *
     * Awaited rather than fired and forgotten. announceLeaveSubmitted catches everything it can
     * fail at, so awaiting cannot make a saved request look unsaved; what it buys is the caller
     * knowing the attempt is over before it reloads the list and closes the form.
     */
    if (serverCreated) {
      await announceLeaveSubmitted(created, {
        managerEmail: input.managerEmail,
        managerName: input.managerName,
      });
    }

    return created;
  }

  /**
   * Apply a change to an existing leave — the approve / reject / cancel path.
   *
   * Server first, because a decision that only lands in the approver's localStorage never
   * reaches the applicant's calendar. The local write is a fallback for offline use and to
   * keep the module working before the resolver is deployed, matching what listLeaves and
   * createLeave already do.
   */
  static async updateLeave(input: {
    id: string;
    status?: LeaveStatus;
    type?: string;
    session?: string;
    startDate?: string;
    endDate?: string;
    reason?: string;
    updatedBy?: string;
    decisionReason?: string;
  }): Promise<LeaveRequest | null> {
    let updated: LeaveRequest | null = null;

    try {
      const result = await getClient().graphql({
        query: updateLeaveMutation,
        variables: { input },
        ...getAuthMode(),
      } as any);
      updated = getData(result)?.updateLeave || null;
    } catch (error) {
      console.warn('[Leave] updateLeave GraphQL failed, falling back to local store:', error);
    }

    // Mirror locally either way: the fallback list is what listLeaves reads when GraphQL is
    // unavailable, so leaving it stale would make the change appear to vanish on reload.
    const local = readLeaves();
    const index = local.findIndex(l => l.id === input.id);
    if (index >= 0) {
      const merged: LeaveRequest = {
        ...local[index],
        ...(input.status !== undefined ? { status: input.status } : {}),
        ...(input.type !== undefined ? { type: input.type } : {}),
        ...(input.session !== undefined ? { session: input.session } : {}),
        ...(input.startDate !== undefined ? { startDate: input.startDate } : {}),
        ...(input.endDate !== undefined ? { endDate: input.endDate } : {}),
        ...(input.reason !== undefined ? { reason: input.reason } : {}),
        // Mirrored too, or the offline path loses the note: the cancellation queue reads
        // decisionReason straight off the list it was given.
        ...(input.decisionReason !== undefined ? { decisionReason: input.decisionReason } : {}),
        ...(input.updatedBy !== undefined ? { updatedBy: input.updatedBy } : {}),
      } as LeaveRequest;
      local[index] = merged;
      writeLeaves(local);
      if (!updated) updated = merged;
    }

    window.dispatchEvent(new Event('leave-requests-updated'));
    window.dispatchEvent(new Event('storage'));
    return updated;
  }

  /**
   * An employee amending their own request.
   *
   * The status is forced back to SUBMITTED, so an already-approved request re-enters the
   * approval queue rather than keeping a decision that was made about different dates. Any
   * previous rejection note is cleared for the same reason — it referred to the old request.
   */
  static async amendLeave(input: {
    id: string;
    type?: string;
    session?: string;
    startDate: string;
    endDate: string;
    reason: string;
    updatedBy?: string;
  }): Promise<LeaveRequest | null> {
    return this.updateLeave({
      ...input,
      status: LeaveStatus.SUBMITTED,
      decisionReason: '',
    });
  }

  static async deleteLeave(id: string): Promise<void> {
    let serverDeleted = false;
    try {
      await getClient().graphql({
        query: deleteLeaveMutation,
        variables: { id },
        ...getAuthMode(),
      } as any);
      serverDeleted = true;
    } catch (error) {
      console.warn('[Leave] deleteLeave GraphQL failed, hiding locally instead:', error);
    }

    // Only fall back to the client-side hide list when the server really refused. Recording
    // it unconditionally is what made deletions look successful while every other employee
    // still saw the leave on the shared calendar.
    if (!serverDeleted) addDeletedId(id);

    const remaining = readLeaves().filter(leave => leave.id !== id);
    writeLeaves(remaining);
    window.dispatchEvent(new Event('leave-requests-updated'));
    window.dispatchEvent(new Event('storage'));
  }
}
